import { View, Text, StyleSheet, TouchableOpacity } from "react-native";
import React from "react";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { Ionicons } from "@expo/vector-icons";
import Colors from "../../../Resources/Color";
import GlobalStyles from "../../../Resources/Styles";

const BlockCard = observer(({ client }) => {
  const { t } = useTranslation();
  const activeBlock = client.summaryStartDate
    ? client.activeBlock(client.summaryStartDate)
    : null;

  // Calculate derived values only if activeBlock exists
  const totalWeeks = activeBlock ? activeBlock.totalWeeks : 0;
  const currentWeek = activeBlock
    ? activeBlock.currentWeek(client.summaryStartDate)
    : 1;
  const createdWeeks = activeBlock ? activeBlock.getWeeks : 0;
  const pendingWeeks = activeBlock ? totalWeeks - createdWeeks : 0;
  const hasNotes =
    activeBlock && activeBlock.notes && activeBlock.notes.trim().length > 0;

  const handlePress = () => {
    if (activeBlock) {
      client.setSelectedBlock(activeBlock);
      const weekToSelect =
        currentWeek > createdWeeks ? createdWeeks : currentWeek;
      activeBlock.setProperty("selectedWeek", weekToSelect);
      activeBlock.setProperty("splitSelectedWeek", weekToSelect);
    }
    client.setProperty("selectedTab", "planification-string");
  };

  return (
    <TouchableOpacity style={GlobalStyles.Card} onPress={handlePress}>
      <View style={GlobalStyles.CardHeader}>
        <View style={GlobalStyles.CardHeaderMain}>
          <View style={GlobalStyles.CardIcon}>
            <Ionicons
              name="clipboard-outline"
              size={20}
              color={Colors.grayColor}
            />
          </View>
          <Text style={GlobalStyles.CardTitle}>{t("currentBlock-string")}</Text>
        </View>
        <Ionicons name="chevron-forward" size={16} color={Colors.grayColor} />
      </View>

      <View style={styles.blockCardTop}>
        <View style={styles.blockMainInfo}>
          {!activeBlock ? (
            <>
              <View style={styles.timelineLabelRow}>
                <Text
                  style={[
                    styles.timelineLabel,
                    { color: Colors.grayColor, fontWeight: "500" },
                  ]}
                >
                  {t("noScheduledWorkouts-string")}
                </Text>
              </View>

              <View style={styles.timelineBarContainer}>
                <View
                  style={[
                    styles.timelineBar,
                    { backgroundColor: Colors.lightGrayColor },
                  ]}
                />
              </View>
            </>
          ) : (
            <>
              <Text style={styles.blockName}>{activeBlock.name}</Text>

              <View style={styles.timelineLabelRow}>
                <Text style={styles.timelineLabel}>
                  {t("weekOfTotal-string")}{" "}
                  <Text
                    style={
                      currentWeek > createdWeeks
                        ? { color: "#C75450" }
                        : undefined
                    }
                  >
                    {currentWeek}
                  </Text>{" "}
                  {t("of-string")} {totalWeeks}
                </Text>
              </View>

              <View style={styles.timelineBarContainer}>
                <View style={styles.timelineBar}>
                  {[...Array(totalWeeks)].map((_, index) => {
                    const weekNumber = index + 1;
                    const isCreated = weekNumber <= createdWeeks;
                    const isCompletedOrCurrent = weekNumber <= currentWeek;
                    const isMissing = !isCreated && isCompletedOrCurrent;

                    return (
                      <View
                        key={index}
                        style={[
                          styles.timelineSegment,
                          isCreated && isCompletedOrCurrent && styles.completed,
                          isCreated && !isCompletedOrCurrent && styles.pending,
                          !isCreated && !isMissing && styles.notCreated,
                          isMissing && { backgroundColor: "#C75450" },
                        ]}
                      />
                    );
                  })}
                </View>
              </View>

              {pendingWeeks > 0 && (
                <View style={styles.pendingWeeksContainer}>
                  <View
                    style={[
                      styles.pendingWeeksInterval,
                      { width: `${(pendingWeeks / totalWeeks) * 100}%` },
                    ]}
                  >
                    <View style={styles.intervalTick} />
                    <View style={styles.intervalDashedLine} />
                    <View style={styles.intervalTick} />
                  </View>
                  <Text style={styles.pendingWeeksText}>
                    {pendingWeeks}{" "}
                    {t(
                      pendingWeeks === 1
                        ? "weekPendingToCreate-string"
                        : "weeksPendingToCreate-string",
                    )}
                  </Text>
                </View>
              )}

              {hasNotes && (
                <View style={styles.blockNotesContainer}>
                  <Text style={styles.blockNotesText} numberOfLines={10}>
                    {activeBlock.notes}
                  </Text>
                </View>
              )}
            </>
          )}
        </View>
      </View>
    </TouchableOpacity>
  );
});

const styles = StyleSheet.create({
  blockCardTop: {
    gap: 12,
  },
  blockMainInfo: {
    gap: 10,
  },
  blockName: {
    fontSize: 18,
    fontWeight: "600",
    letterSpacing: 0.2,
    color: Colors.blackColor,
    marginBottom: 8,
  },
  timelineLabelRow: {
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    marginBottom: 4,
  },
  timelineLabel: {
    fontSize: 13,
    fontWeight: "600",
    color: Colors.blackColor,
    textAlign: "center",
  },
  timelineBarContainer: {
    width: "100%",
    paddingHorizontal: 0,
    marginTop: 2,
    marginBottom: 2,
  },
  timelineBar: {
    position: "relative",
    flexDirection: "row",
    height: 6,
    width: "100%",
    borderRadius: 6,
    overflow: "hidden",
    gap: 2,
  },
  timelineSegment: {
    height: "100%",
    borderRadius: 2,
    flex: 1,
  },
  completed: {
    backgroundColor: Colors.bubbleColor,
  },
  pending: {
    backgroundColor: "#D1D5DB",
  },
  notCreated: {
    backgroundColor: "#D1D5DB",
    opacity: 1,
  },
  pendingWeeksContainer: {
    marginTop: 8,
    alignItems: "flex-end",
    // paddingRight: 4,
  },
  pendingWeeksInterval: {
    position: "absolute",
    right: 0,
    top: -8,
    width: "33.33%",
    flexDirection: "row",
    alignItems: "center",
  },
  intervalTick: {
    width: 1,
    height: 6,
    backgroundColor: Colors.grayColor,
  },
  intervalDashedLine: {
    flex: 1,
    height: 1,
    borderBottomWidth: 1,
    borderStyle: "dashed",
    borderColor: Colors.grayColor,
  },
  pendingWeeksText: {
    fontSize: 10,
    color: Colors.grayColor,
    marginTop: 4,
  },
  blockNotesContainer: {
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: Colors.lightGrayColor,
  },
  blockNotesText: {
    fontSize: 12,
    color: Colors.mediumGray,
    fontStyle: "italic",
    lineHeight: 16,
  },
});

export default BlockCard;
